// Contingency hourly-fee guard — the frontend half (feedback #837, round 2).
//
// On a contingency matter the client's WRITTEN contingency fee agreement
// governs what the firm may charge (Cal. Rules of Prof. Conduct, rule 1.5;
// Bus. & Prof. Code § 6147).  So the server refuses to create an invoice that
// carries chargeable hourly time on a contingency matter — HTTP 409
// { code: 'contingency_hourly_fee', error, matters: [{ id, title, fee_amount }] }
// — unless the request carries contingency_fee_override_reason (20–500
// characters), which it records in the audit log.
//
// Every caller of POST /api/invoices/generate and POST /api/invoices/batch
// goes through submitWithContingencyGuard so the handling is identical:
// ask for the reason, explain why it is needed and that it is recorded, then
// resubmit the SAME request with the reason.  Cancel creates nothing and is
// not an error (the caller shows no error message).
//
// The reason is given for the matters and fees the 409 SHOWED, so the
// resubmit carries that list back too (contingency_fee_override_matters:
// [{ id, fee_amount }]).  The server bills a contingency matter hourly only if
// it was on that list at no more than the fee shown; if a matter was added or
// a fee rose while the reason was being written, it answers 409 again with
// `changed`, and we ask again showing what changed — never silently.

import { fmtMoney } from './matterTimeReports'

export const CONTINGENCY_HOURLY_FEE_CODE = 'contingency_hourly_fee'
export const OVERRIDE_REASON_MIN = 20
export const OVERRIDE_REASON_MAX = 500

export function isContingencyHourlyFeeError(err) {
  return err?.response?.status === 409 && err?.response?.data?.code === CONTINGENCY_HOURLY_FEE_CODE
}

const feeText = (amount) => {
  const n = Number(amount)
  return Number.isFinite(n) ? fmtMoney(Math.round(n * 100)) : null
}

// After a run of re-prompts this many times in a row the server's error is
// shown instead (the list keeps changing under the user; nothing is created).
export const MAX_CONFIRM_ROUNDS = 5

// The list the reason is given for, exactly as the server sent it.
export function acknowledgedMatters(data = {}) {
  const matters = Array.isArray(data.matters) ? data.matters : []
  return matters
    .filter(m => m && m.id != null && m.fee_amount != null && m.fee_amount !== '' && Number.isFinite(Number(m.fee_amount)))
    .map(m => ({ id: m.id, fee_amount: m.fee_amount }))
}

function changedLines(changed) {
  return changed.map(c => {
    const name = c.title || `Matter #${c.id}`
    const now = feeText(c.fee_amount)
    if (c.acknowledged_fee_amount == null) {
      return `• ${name} — not on the list you gave your reason for${now ? ` (${now} in hourly fees)` : ''}`
    }
    const was = feeText(c.acknowledged_fee_amount)
    return `• ${name} — now ${now || 'more'}${was ? `, not the ${was} you gave your reason for` : ''}`
  }).join('\n')
}

export function contingencyPromptMessage(data = {}, { previousLength } = {}) {
  const parts = []
  if (previousLength != null) {
    parts.push(`Please enter at least ${OVERRIDE_REASON_MIN} characters — you entered ${previousLength}.`)
  }
  const matters = Array.isArray(data.matters) ? data.matters : []
  const changed = Array.isArray(data.changed) ? data.changed : []
  if (changed.length) {
    parts.push('The list changed after you gave your reason:')
    parts.push(changedLines(changed))
    parts.push('Check every matter below and confirm your reason covers each of them, or change it.')
  }
  if (matters.length) {
    // Built from the structured fields.  The server's error sentence lists
    // the same matters and the same rules inline, so showing it too would
    // say everything twice; it is the fallback when no list came back.
    parts.push(matters.length > 1
      ? 'No invoices were created. These contingency matters have hourly fees selected:'
      : 'No invoice was created. This contingency matter has hourly fees selected:')
    parts.push(matters.map(m => {
      const fee = feeText(m.fee_amount)
      return `• ${m.title || `Matter #${m.id}`}${fee ? ` — ${fee} in hourly fees` : ''}`
    }).join('\n'))
  } else {
    parts.push(data.error || 'This invoice includes hourly fees on a contingency matter.')
  }
  parts.push(
    "The client's written contingency fee agreement governs the fees on " +
    (matters.length > 1 ? 'these matters' : 'this matter') +
    ' (Cal. Rules of Prof. Conduct, rule 1.5; Bus. & Prof. Code § 6147). ' +
    'To bill hourly time anyway, write the reason it is permitted (for example, the agreement provides for it). ' +
    'Your reason is recorded in the audit log with the invoice.'
  )
  parts.push(`Reason (${OVERRIDE_REASON_MIN}–${OVERRIDE_REASON_MAX} characters):`)
  return parts.join('\n\n')
}

// Resolves to the trimmed reason, or null if the user cancelled.  Re-asks
// (saying why) until the reason is long enough; the input caps the length.
export async function askContingencyOverrideReason(ask, data = {}, { confirmLabel = 'Create invoice', initialReason = '' } = {}) {
  let previousLength
  let value = initialReason || ''
  for (;;) {
    const answer = await ask({
      type: 'text',
      title: 'Hourly fees on a contingency matter',
      message: contingencyPromptMessage(data, { previousLength }),
      defaultValue: value,
      placeholder: 'Why hourly billing is permitted on this matter',
      confirmLabel,
      cancelLabel: 'Cancel',
      maxLength: OVERRIDE_REASON_MAX,
      required: true,
    })
    if (answer == null) return null
    value = String(answer)
    const reason = value.trim()
    if (reason.length >= OVERRIDE_REASON_MIN) return reason.slice(0, OVERRIDE_REASON_MAX)
    previousLength = reason.length
  }
}

// submit(extraBody) must send the SAME request each time, merged with
// extraBody.  Resolves { response } on success, { cancelled: true } when the
// user declined to give a reason.  Any other error is re-thrown unchanged.
// A further 'contingency_hourly_fee' 409 (the list changed while the reason
// was being written) asks again, showing what changed, with the last reason
// pre-filled so the user can confirm or edit it.
export async function submitWithContingencyGuard(ask, submit, opts = {}) {
  let extra = {}
  let lastReason = ''
  for (let round = 0; ; round++) {
    try {
      return { response: await submit(extra) }
    } catch (err) {
      if (!isContingencyHourlyFeeError(err) || round >= MAX_CONFIRM_ROUNDS) throw err
      const data = err.response.data || {}
      const reason = await askContingencyOverrideReason(ask, data, { ...opts, initialReason: lastReason })
      if (reason == null) return { cancelled: true }
      lastReason = reason
      extra = {
        contingency_fee_override_reason: reason,
        contingency_fee_override_matters: acknowledgedMatters(data),
      }
    }
  }
}
